import { BreadCrumbPremium } from 'app/components/BreadCrumbPremium';
import LoadPanel from 'app/components/LoadPanel';
import React, { useEffect, useRef, useState } from 'react';
import { notification } from 'utils/notification';
import { Form, Template } from 'devextreme-react';
import useAxios from 'axios-hooks';
import LabelTextTemplate from 'app/components/Templates/LabelTextTemplate';
import PopupConfirmWithRef from 'app/components/PopupCommon/PopupConfirmWithRef/PopupConfirmWithRef';
import './styles.scss';
import useFormatTime from 'hooks/useFormatTime';
import { CONSTANT } from 'constant';
import { restrictKeyboardPhone } from 'utils/common/restrict-keyboard-phone';
import useDictionary from 'hooks/useDictionary';
import { WrapContent } from 'app/components/WrapContent';
import { requiredMessage } from 'utils/messages';

const UserProfile = () => {
  const { t }: any = useDictionary({ programId: 'user-profile' });
  const formRef: any = useRef(null);
  const confirmSaveRef: any = useRef(null);
  const { listPhone } = CONSTANT;
  const { DateFormat } = useFormatTime();
  const [formData, setFormData] = useState<any>({
    userId: null,
    userTp: '',
    userFullNm: '',
    userGender: '',
    userNm: '',
    userRole: '',
    birthDt: '',
    loginId: '',
    activeTf: true,
    placeIssue: '',
    joinDm: '',
    withdrawDm: '',
    userEmail: '',
    phoneCode: '',
    userPhone: '',
    mobileCode: '',
    userMobile: '',
    userDepart: '',
    userPosition: '',
    userLevel: '',
    userNt: '',
  });

  const [{ loading }, refetchData] = useAxios(
    {},
    { manual: true, autoCancel: true, useCache: false },
  );

  /**
   * Get data detail
   *
   */
  useEffect(() => {
    const getDetail = async () => {
      const res = await refetchData({
        url: `/user-manager/get-user-profile`,
        method: 'GET',
      });
      if (res?.data?.status === '200') {
        const index = 5;
        const resData = res?.data?.data || {};
        const phoneCode = resData?.userPhone?.slice(0, index) || '';
        const userPhone =
          resData?.userPhone?.slice(index, resData?.userPhone?.length) || '';
        const mobileCode = resData?.userMobile?.slice(0, index) || '';
        const userMobile =
          resData?.userMobile?.slice(index, resData?.userMobile?.length) || '';

        setFormData({
          ...resData,
          phoneCode,
          userPhone,
          mobileCode,
          userMobile,
        });
      } else {
        notification({ res });
      }
    };
    getDetail();
  }, []);

  /**
   * Validate save
   *
   * @return {*}
   */
  const validateSave = () => {
    const isValid = formRef?.current?.instance?.validate?.()?.isValid;
    if (!isValid) return;
    confirmSaveRef?.current?.onOpen?.();
  };
  /**
   * on submit
   *
   * @return {*}
   */
  const onSave = async () => {
    const dataRequest = {
      ...formData,
      userPhone: formData?.userPhone
        ? formData?.phoneCode + formData?.userPhone
        : '',
      userMobile: formData?.userMobile
        ? formData?.mobileCode + formData?.userMobile
        : '',
    };
    const res = await refetchData({
      url: `/user-manager/update-profile-user`,
      method: 'PUT',
      data: dataRequest,
    });
    notification({ res });
  };

  return (
    <WrapContent>
      <React.Suspense fallback={<LoadPanel visible={true} />}>
        <LoadPanel visible={loading} />
        <BreadCrumbPremium
          title={t('User Profile')}
          subTitle={t('view details here.')}
          onSubmit={() => validateSave()}
        />
        <div className="user-profile-form-main">
          <div className="user-profile-form-content">
            <Form
              ref={formRef}
              labelLocation="top"
              showColonAfterLabel={false}
              className="form-label-bold"
              formData={formData}
              colCount={1}
              items={[
                {
                  itemType: 'group',
                  colCount: 6,
                  items: [
                    {
                      itemType: 'simple',
                      template: 'generalInformation',
                      colSpan: 2,
                      cssClass: 'general-Info-form-item',
                    },
                    {
                      itemType: 'group',
                      colSpan: 4,
                      colCount: 2,
                      items: [
                        {
                          label: { text: t('Full Name') },
                          dataField: 'userFullNm',
                          editorType: 'dxTextBox',
                          validationRules: [
                            { type: 'stringLength', max: 256 },
                            {
                              type: 'required',
                              message: requiredMessage(t('Full Name')),
                            },
                          ],
                        },
                        {
                          dataField: 'userId',
                          editorType: 'dxTextBox',
                          label: { text: t('ID') },
                          editorOptions: {
                            disabled: true,
                          },
                        },
                        {
                          dataField: 'userGender',
                          editorType: 'dxSelectBox',
                          label: { text: t('Gender') },
                          editorOptions: {
                            displayExpr: 'name',
                            valueExpr: 'value',
                            defaultValue: '',
                            items: [
                              {
                                name: t('Male'),
                                value: '0',
                              },
                              {
                                name: t('Female'),
                                value: '1',
                              },
                              {
                                name: t('Other'),
                                value: '2',
                              },
                            ],
                          },
                        },
                        {
                          dataField: 'birthDt',
                          editorType: 'dxDateBox',
                          label: { text: t('Date Of Birth') },
                          editorOptions: {
                            max: new Date(),
                            displayFormat: DateFormat,
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  itemType: 'group',
                  colCount: 6,
                  caption: ' ',
                  items: [
                    {
                      itemType: 'simple',
                      template: 'accountInformation',
                      colSpan: 2,
                      cssClass: 'general-Info-form-item',
                    },
                    {
                      itemType: 'group',
                      colSpan: 4,
                      colCount: 2,
                      items: [
                        {
                          label: { text: t('User Name') },
                          dataField: 'loginId',
                          editorType: 'dxTextBox',
                          validationRules: [
                            { type: 'stringLength', max: 50 },
                            { type: 'required' },
                          ],
                          editorOptions: {
                            disabled: true,
                          },
                        },
                        {
                          label: { text: t('Role') },
                          dataField: 'userRole',
                          editorType: 'dxTextBox',
                          validationRules: [{ type: 'stringLength', max: 256 }],
                        },
                      ],
                    },
                  ],
                },
                {
                  itemType: 'group',
                  colCount: 6,
                  caption: ' ',
                  items: [
                    {
                      itemType: 'simple',
                      template: 'userContact',
                      colSpan: 2,
                      cssClass: 'general-Info-form-item',
                    },
                    {
                      itemType: 'group',
                      colSpan: 4,
                      colCount: 1,
                      items: [
                        {
                          label: { text: t('E-mail') },
                          dataField: 'userEmail',
                          editorType: 'dxTextBox',
                          editorOptions: {
                            mode: 'email',
                          },
                          validationRules: [
                            { type: 'email' },
                            {
                              type: 'required',
                              message: requiredMessage(t('E-mail')),
                            },
                            { type: 'stringLength', max: 256 },
                          ],
                        },
                        {
                          itemType: 'group',
                          colCount: 3,
                          items: [
                            {
                              dataField: 'phoneCode',
                              editorType: 'dxSelectBox',
                              label: {
                                text: t('Phone No'),
                              },
                              editorOptions: {
                                displayExpr: 'label',
                                valueExpr: 'value',
                                items: listPhone,
                              },
                            },
                            {
                              dataField: 'userPhone',
                              editorType: 'dxTextBox',
                              colSpan: 2,
                              label: {
                                text: t('Phone Number'),
                              },
                              editorOptions: {
                                onKeyDown: restrictKeyboardPhone,
                              },
                            },
                          ],
                        },
                        {
                          itemType: 'group',
                          colCount: 3,
                          items: [
                            {
                              dataField: 'mobileCode',
                              editorType: 'dxSelectBox',
                              label: {
                                text: t('Mobile No'),
                              },
                              editorOptions: {
                                displayExpr: 'label',
                                valueExpr: 'value',
                                items: listPhone,
                              },
                            },
                            {
                              dataField: 'userMobile',
                              editorType: 'dxTextBox',
                              colSpan: 2,
                              label: {
                                text: t('Mobile Number'),
                              },
                              editorOptions: {
                                onKeyDown: restrictKeyboardPhone,
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  itemType: 'group',
                  colCount: 6,
                  caption: ' ',
                  items: [
                    {
                      itemType: 'simple',
                      template: 'jobInformation',
                      colSpan: 2,
                      cssClass: 'general-Info-form-item',
                    },
                    {
                      itemType: 'group',
                      colSpan: 4,
                      colCount: 2,
                      items: [
                        {
                          label: { text: t('Department') },
                          dataField: 'userDepart',
                          editorType: 'dxTextBox',
                          validationRules: [{ type: 'stringLength', max: 256 }],
                        },
                        {
                          label: { text: t('Position') },
                          dataField: 'userPosition',
                          editorType: 'dxTextBox',
                          validationRules: [{ type: 'stringLength', max: 256 }],
                        },
                        {
                          label: { text: t('Level') },
                          dataField: 'userLevel',
                          editorType: 'dxTextBox',
                          validationRules: [{ type: 'stringLength', max: 256 }],
                        },
                        {
                          dataField: 'joinDm',
                          editorType: 'dxDateBox',
                          label: { text: t('Start Working') },
                          editorOptions: {
                            max: new Date(),
                            displayFormat: DateFormat,
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  itemType: 'group',
                  colCount: 6,
                  caption: ' ',
                  items: [
                    {
                      itemType: 'simple',
                      template: 'noteTemplate',
                      colSpan: 2,
                      cssClass: 'general-Info-form-item',
                    },
                    {
                      itemType: 'group',
                      colSpan: 4,
                      colCount: 1,
                      items: [
                        {
                          label: { text: t('Note') },
                          dataField: 'userNt',
                          editorType: 'dxTextArea',
                          editorOptions: {
                            height: 60,
                          },
                          validationRules: [{ type: 'stringLength', max: 500 }],
                        },
                      ],
                    },
                  ],
                },
              ]}
            >
              <Template
                name="generalInformation"
                component={() => (
                  <LabelTextTemplate
                    title={t('General Information')}
                    subTitle={t('This will be displayed on your profile.')}
                    height={40}
                  />
                )}
              />
              <Template
                name="accountInformation"
                component={() => (
                  <LabelTextTemplate
                    title={t('Account Information')}
                    subTitle={t('Set up information for signing in.')}
                    height={40}
                  />
                )}
              />
              <Template
                name="userContact"
                component={() => (
                  <LabelTextTemplate
                    title={t('User Contact')}
                    subTitle={t(
                      'These contact methods will be displayed on users profile.',
                    )}
                    height={40}
                  />
                )}
              />
              <Template
                name="jobInformation"
                component={() => (
                  <LabelTextTemplate
                    title={t('Job Information')}
                    subTitle={t('This will be displayed on your profile.')}
                    height={40}
                  />
                )}
              />
              <Template
                name="noteTemplate"
                component={() => (
                  <LabelTextTemplate
                    title={t('Note')}
                    subTitle={t('This will be displayed on your profile.')}
                    height={40}
                  />
                )}
              />
            </Form>
            <PopupConfirmWithRef
              ref={confirmSaveRef}
              content={'Are you sure to save?'}
              isTranslate
              onOk={onSave}
            />
          </div>
        </div>
      </React.Suspense>
    </WrapContent>
  );
};

export default UserProfile;
